<script setup lang="ts">
import QRCode from 'qrcode'

const props = defineProps<{
  pairingCode: string
  pairingUrl: string
  presenterEntryUrl: string
}>()

const emit = defineEmits<{
  regenerate: []
}>()

const { data: ui } = useChallengeWheelUI()

const qrDataUrl = ref<string>('')

// Human-friendly rendering of the presenter entry URL — drop protocol and any
// trailing slash so it fits on one line and can be read aloud.
const displayEntryUrl = computed(() => {
  const raw = props.presenterEntryUrl
  if (!raw) {
    return ''
  }
  try {
    const parsed = new URL(raw)
    return `${parsed.host}${parsed.pathname.replace(/\/$/, '')}`
  } catch {
    return raw
  }
})

watch(
  () => props.pairingUrl,
  async url => {
    if (!url) {
      qrDataUrl.value = ''
      return
    }
    try {
      qrDataUrl.value = await QRCode.toDataURL(url, {
        errorCorrectionLevel: 'M',
        margin: 1,
        scale: 6,
      })
    } catch {
      qrDataUrl.value = ''
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="space-y-4">
    <div class="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex flex-1 flex-col items-center gap-3 sm:items-start">
        <div class="flex flex-col items-center gap-1.5 sm:items-start">
          <span class="text-xs uppercase tracking-wide text-muted">
            {{ ui?.setup.presentation.pairingCodeLabel }}
          </span>
          <div class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-[1.5px] border-default bg-elevated/50">
            <code class="text-2xl font-mono font-bold tracking-[0.3em] text-primary">
              {{ pairingCode || '—' }}
            </code>
            <div class="w-px h-6 bg-default/50"/>
            <UButton
              color="neutral"
              icon="mdi:refresh"
              size="xs"
              variant="ghost"
              :aria-label="ui?.setup.presentation.pairingRegenerateAriaLabel"
              :disabled="!pairingCode"
              @click="emit('regenerate')"
            />
          </div>
        </div>

        <div
          v-if="displayEntryUrl"
          class="flex flex-col items-center gap-1 sm:items-start"
        >
          <span class="text-xs uppercase tracking-wide text-muted">
            {{ ui?.setup.presentation.pairingUrlLabel }}
          </span>
          <code class="rounded-md bg-elevated px-2 py-1 text-sm font-mono">
            {{ displayEntryUrl }}
          </code>
        </div>
      </div>

      <div
        v-if="qrDataUrl"
        class="shrink-0 rounded-md bg-white p-2 shadow-sm"
      >
        <img
          class="size-32 sm:size-36"
          :alt="ui?.setup.presentation.pairingQrAlt"
          :src="qrDataUrl"
        >
      </div>
    </div>

    <p class="text-xs text-muted">
      {{ ui?.setup.presentation.pairingHint }}
    </p>
  </div>
</template>
